import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  Box,
  Divider,
  Icon,
  Spinner,
  Alert,
  AlertIcon,
  SimpleGrid,
  Image,
  useDisclosure,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { FaClock, FaUser, FaEdit } from 'react-icons/fa';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}`;

interface HistorialItem {
  listoricoId: number;
  licenciaId: number;
  usuarioId: number;
  nombreUsuario: string;
  ciudadano: string;
  nombreEstablecimiento: string;
  ine?: string;
  giroVinosLicoresId: number;
  tipo: string;
  estatus: string;
  movimiento: string;
  horario: string;
  direccion: string;
  localidad: string;
  calle: string;
  numero: string;
  codigoPostal: string;
  lat: number;
  lng: number;
  folioLicencia: string;
  montoLicencia: string;
  fechaRegistro?: string;
  referenciaPago: string;
  comprobantePago?: string;
  fechaPago?: string;
  pagado: boolean;
  descripcion: string;
  creadoEn: string;
  actualizadoEn: string;
  archivosINE?: Array<{
    id: number;
    url: string;
    tipo: string;
    descripcion: string;
  }>;
  comprobantesPago?: Array<{
    id: number;
    url: string;
    tipo: string;
    descripcion: string;
  }>;
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  licenciaId: number | null;
}

const ModalHistorialVinosLicores = ({ isOpen, onClose, licenciaId }: ModalProps) => {
  const [historial, setHistorial] = useState<HistorialItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imagenSeleccionada, setImagenSeleccionada] = useState<string>('');
  const { isOpen: isImageModalOpen, onOpen: onImageModalOpen, onClose: onImageModalClose } = useDisclosure();

  useEffect(() => {
    if (isOpen && licenciaId) {
      fetchHistorial();
    }
  }, [isOpen, licenciaId]);

  const fetchHistorial = async () => {
    if (!licenciaId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/licencias-vinos-licores/${licenciaId}/historico`);
      if (response.ok) {
        const data = await response.json();
        setHistorial(data);
      } else {
        throw new Error('Error al cargar el historial');
      }
    } catch (error) {
      console.error('Error al cargar historial:', error);
      setError('Error al cargar el historial de la licencia');
      setHistorial([]);
    } finally {
      setLoading(false);
    }
  };

  const handleArchivoClick = (url: string, tipo: string) => {
    const fullUrl = `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/uploads/${url.split('uploads\\')[1]?.replace(/\\/g, '/')}`;
    if (tipo === 'pdf') {
      window.open(fullUrl, '_blank');
    } else {
      setImagenSeleccionada(fullUrl);
      onImageModalOpen();
    }
  };

  const formatearFecha = (fechaISO: string) => {
    if (!fechaISO) return 'Fecha no disponible';
    
    const fecha = new Date(fechaISO);
    if (isNaN(fecha.getTime())) {
      return 'Fecha inválida';
    }
    
    return fecha.toLocaleString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getChangedFields = (current: HistorialItem, previous: HistorialItem): string[] => {
    const fieldsToCompare = Object.keys(current).filter(key => key !== 'listoricoId' && key !== 'creadoEn' && key !== 'actualizadoEn');
    return fieldsToCompare.filter(field => current[field as keyof HistorialItem] !== previous[field as keyof HistorialItem]);
  };

  const hasValue = (value: any): boolean => {
    return value !== null && value !== undefined && value !== '' && value !== 0;
  };

  const FieldDisplay = ({ label, value, isChanged }: { label: string; value: string; isChanged: boolean }) => {
    if (!hasValue(value)) return null;
    return (
      <Text 
        fontSize="sm" 
        bg={isChanged ? "yellow.100" : "transparent"}
        p={isChanged ? 2 : 0}
        borderRadius={isChanged ? "md" : "none"}
        border={isChanged ? "1px solid" : "none"}
        borderColor={isChanged ? "yellow.400" : "transparent"}
      >
        <strong>{label}:</strong> {value}
        {isChanged && <Badge ml={2} colorScheme="yellow" size="sm">CAMBIÓ</Badge>}
      </Text>
    );
  };



  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          bgImage="url('/img/bg.webp')"
          bgSize="cover"
          bgPosition="center"
          bgRepeat="no-repeat"
          color="white"
          textShadow="2px 2px 4px rgba(0,0,0,0.7)"
          fontWeight="bold"
          position="relative"
        >
          <Icon as={FaClock} mr={2} />
          Historial de la Licencia
        </ModalHeader>
        <ModalCloseButton color="white" _hover={{ bg: 'rgba(255,255,255,0.2)' }} />
        
        <ModalBody>
          {loading && (
            <Box textAlign="center" py={8}>
              <Spinner size="lg" color="#5b182b" />
              <Text mt={4}>Cargando historial...</Text>
            </Box>
          )}

          {error && (
            <Alert status="error" mb={4}>
              <AlertIcon />
              {error}
            </Alert>
          )}

          {!loading && !error && historial.length === 0 && (
            <Alert status="info">
              <AlertIcon />
              No hay historial disponible para esta licencia.
            </Alert>
          )}

          {!loading && historial.length > 0 && (
            <VStack spacing={4} align="stretch">
              {historial.map((item, index) => (
                <Box 
                  key={item.listoricoId}
                  p={4}
                  borderRadius="md"
                  border={index === 0 ? "2px solid" : "1px solid"}
                  borderColor={index === 0 ? "blue.400" : "gray.200"}
                  bg={index === 0 ? "blue.50" : "white"}
                >
                  <HStack justify="space-between" mb={3}>
                    <Badge colorScheme={index === 0 ? "blue" : "gray"}>
                      {index === 0 ? "ÚLTIMO CAMBIO" : "REGISTRO"}
                    </Badge>
                    <Text fontSize="sm" color="gray.500" fontWeight="medium">
                      {formatearFecha(item.creadoEn)}
                    </Text>
                  </HStack>
                  
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                    {(() => {
                      const changedFields = index === 0 && historial[1] ? getChangedFields(item, historial[1]) : [];
                      return (
                        <>
                          <FieldDisplay label="Ciudadano" value={item.ciudadano} isChanged={changedFields.includes('ciudadano')} />
                          <FieldDisplay label="Establecimiento" value={item.nombreEstablecimiento} isChanged={changedFields.includes('nombreEstablecimiento')} />
                          <FieldDisplay label="Usuario" value={item.nombreUsuario} isChanged={changedFields.includes('nombreUsuario')} />
                          <FieldDisplay label="INE" value={item.ine} isChanged={changedFields.includes('ine')} />
                          <FieldDisplay label="Giro ID" value={item.giroVinosLicoresId?.toString()} isChanged={changedFields.includes('giroVinosLicoresId')} />
                          <FieldDisplay label="Tipo" value={item.tipo} isChanged={changedFields.includes('tipo')} />
                          <FieldDisplay label="Estatus" value={item.estatus} isChanged={changedFields.includes('estatus')} />
                          <FieldDisplay label="Movimiento" value={item.movimiento} isChanged={changedFields.includes('movimiento')} />
                          <FieldDisplay label="Horario" value={item.horario} isChanged={changedFields.includes('horario')} />
                          <FieldDisplay label="Localidad" value={item.localidad} isChanged={changedFields.includes('localidad')} />
                          <FieldDisplay label="Calle" value={item.calle} isChanged={changedFields.includes('calle')} />
                          <FieldDisplay label="Número" value={item.numero} isChanged={changedFields.includes('numero')} />
                          <FieldDisplay label="Código Postal" value={item.codigoPostal} isChanged={changedFields.includes('codigoPostal')} />
                          <FieldDisplay label="Latitud" value={item.lat?.toString()} isChanged={changedFields.includes('lat')} />
                          <FieldDisplay label="Longitud" value={item.lng?.toString()} isChanged={changedFields.includes('lng')} />
                          <FieldDisplay label="Folio" value={item.folioLicencia} isChanged={changedFields.includes('folioLicencia')} />
                          <FieldDisplay label="Monto" value={`$${item.montoLicencia}`} isChanged={changedFields.includes('montoLicencia')} />
                          <FieldDisplay label="Fecha Registro" value={item.fechaRegistro ? formatearFecha(item.fechaRegistro) : ''} isChanged={changedFields.includes('fechaRegistro')} />
                          <FieldDisplay label="Referencia Pago" value={item.referenciaPago} isChanged={changedFields.includes('referenciaPago')} />
                          <FieldDisplay label="Comprobante Pago" value={item.comprobantePago} isChanged={changedFields.includes('comprobantePago')} />
                          <FieldDisplay label="Fecha Pago" value={item.fechaPago ? formatearFecha(item.fechaPago) : ''} isChanged={changedFields.includes('fechaPago')} />
                          <FieldDisplay label="Pagado" value={item.pagado ? 'Sí' : 'No'} isChanged={changedFields.includes('pagado')} />
                        </>
                      );
                    })()} 
                  </SimpleGrid>
                  
                  {(() => {
                    const changedFields = index === 0 && historial[1] ? getChangedFields(item, historial[1]) : [];
                    return (
                      <Box mt={2}>
                        <FieldDisplay label="Dirección" value={item.direccion} isChanged={changedFields.includes('direccion')} />
                      </Box>
                    );
                  })()}
                  
                  {(() => {
                    const changedFields = index === 0 && historial[1] ? getChangedFields(item, historial[1]) : [];
                    return (
                      <Box mt={2}>
                        <FieldDisplay label="Descripción" value={item.descripcion} isChanged={changedFields.includes('descripcion')} />
                      </Box>
                    );
                  })()}
                  
                  {/* Sección de Archivos */}
                  <Box mt={3}>
                    <Text fontSize="sm" fontWeight="bold" mb={2}>Documentos:</Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                      <Box>
                        <Text fontSize="xs" fontWeight="medium" mb={1}>Archivos INE:</Text>
                        {item.archivosINE && item.archivosINE.length > 0 ? (
                          item.archivosINE.map((archivo: any, idx: number) => (
                            <Button
                              key={idx}
                              size="xs"
                              variant="outline"
                              colorScheme="blue"
                              mb={1}
                              mr={1}
                              onClick={() => handleArchivoClick(archivo.url, archivo.tipo)}
                            >
                              {archivo.tipo === 'pdf' ? '📄' : '🖼️'} INE {idx + 1}
                            </Button>
                          ))
                        ) : (
                          <Text fontSize="xs" color="gray.500">Sin archivos INE</Text>
                        )}
                      </Box>
                      <Box>
                        <Text fontSize="xs" fontWeight="medium" mb={1}>Comprobantes:</Text>
                        {item.comprobantesPago && item.comprobantesPago.length > 0 ? (
                          item.comprobantesPago.map((comprobante: any, idx: number) => (
                            <Button
                              key={idx}
                              size="xs"
                              variant="outline"
                              colorScheme="green"
                              mb={1}
                              mr={1}
                              onClick={() => handleArchivoClick(comprobante.url, comprobante.tipo)}
                            >
                              {comprobante.tipo === 'pdf' ? '📄' : '🖼️'} {comprobante.descripcion}
                            </Button>
                          ))
                        ) : (
                          <Text fontSize="xs" color="gray.500">Sin comprobantes</Text>
                        )}
                      </Box>
                    </SimpleGrid>
                  </Box>
                  
                  {index === 0 && historial[1] && (() => {
                    const changedFields = getChangedFields(item, historial[1]);
                    return changedFields.length > 0 && (
                      <Box mt={2} p={2} bg="yellow.50" borderRadius="md" border="1px solid" borderColor="yellow.200">
                        <Text fontSize="xs" fontWeight="bold" color="yellow.700">
                          Campos modificados ({changedFields.length}): {changedFields.join(', ')}
                        </Text>
                      </Box>
                    );
                  })()}
                  
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    Registrado: {formatearFecha(item.creadoEn)}
                    {item.actualizadoEn && item.actualizadoEn !== item.creadoEn && (
                      <> | Actualizado: {formatearFecha(item.actualizadoEn)}</>
                    )}
                  </Text>
                  
                  {index < historial.length - 1 && <Divider mt={4} />}
                </Box>
              ))}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose}>Cerrar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
    
    <Modal isOpen={isImageModalOpen} onClose={onImageModalClose} size="6xl">
      <ModalOverlay bg="blackAlpha.800" />
      <ModalContent bg="transparent" boxShadow="none">
        <ModalCloseButton color="white" size="lg" _hover={{ bg: 'rgba(255,255,255,0.2)' }} />
        <ModalBody p={0} display="flex" justifyContent="center" alignItems="center">
          <Image
            src={imagenSeleccionada}
            alt="Imagen ampliada"
            maxW="100%"
            maxH="90vh"
            objectFit="contain"
            borderRadius="md"
          />
        </ModalBody>
      </ModalContent>
    </Modal>
    </>
  );
};

export default ModalHistorialVinosLicores;